import { useState, useEffect, useCallback } from 'react';
import { useUrlState } from '../hooks/useUrlParams';
import { vehicleInfoAPI } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import FilterBar, { SearchField, FilterSelect, ResetButton, CountPill } from '../components/FilterBar';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  // The filters read as one control group on the left; only the count is
  // pushed right. space-between across all four left canyons between them.
  // Active filters share one accent so it is obvious at a glance which of them
  // is narrowing the table.
  emptyAction: { marginTop: '14px', padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', color: '#4f46e5', fontSize: '13px', fontWeight: '500', cursor: 'pointer', fontFamily: 'inherit' },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '1000px' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  thRight: { textAlign: 'right' },
  td: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  tdRight: { padding: '14px 16px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', verticalAlign: 'middle' },
  plate: { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: '600', color: '#1e293b' },
  muted: { color: '#94a3b8' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', display: 'inline-block' },
  statusActive: { backgroundColor: '#dcfce7', color: '#166534' },
  statusOther: { backgroundColor: '#fef3c7', color: '#92400e' },
  actionButtons: { display: 'inline-flex', gap: '6px', justifyContent: 'flex-end' },
  iconButton: { padding: '7px', border: '1px solid #e2e8f0', borderRadius: '8px', backgroundColor: '#fff', cursor: 'pointer', color: '#64748b', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s' },
  emptyState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  loadingState: { textAlign: 'center', padding: '80px 20px', color: '#64748b' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', margin: '16px', fontSize: '14px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: '12px' },
  pageInfo: { fontSize: '13px', color: '#64748b' },
  pageButtons: { display: 'flex', gap: '4px' },
  pageButton: { padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#fff', cursor: 'pointer', fontSize: '13px', color: '#475569' },
  pageButtonDisabled: { opacity: 0.5, cursor: 'not-allowed' },

  // Modal
  modal: { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '760px', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 25px 60px rgba(0,0,0,0.25)' },
  modalHeader: { padding: '18px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '700', color: '#1e293b', margin: 0 },
  modalBody: { padding: '20px 24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#fafbfc' },
  closeBtn: { width: '32px', height: '32px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sectionTitle: { fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '20px 0 10px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '12px' },
  field: { border: '1px solid #f1f5f9', borderRadius: '10px', padding: '10px 12px', backgroundColor: '#fafbfc' },
  fieldLabel: { fontSize: '11px', color: '#94a3b8', marginBottom: '3px' },
  fieldValue: { fontSize: '14px', color: '#1e293b', wordBreak: 'break-word' },
  primaryButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  secondaryButton: { padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
};

// Order and labels for the columns worth showing in the detail view. Anything
// not listed here that still has a value lands in the "Other Details" section.
const DETAIL_FIELDS = [
  ['registrationNo', 'Registration No'],
  ['ownerName', 'Owner Name'],
  ['fatherName', 'Father Name'],
  ['makerModel', 'Maker / Model'],
  ['maker', 'Maker'],
  ['model', 'Model'],
  ['vehicleClass', 'Vehicle Class'],
  ['vehicleType', 'Category'],
  ['fuelType', 'Fuel Type'],
  ['fuelNorms', 'Norms'],
  ['registrationDate', 'Registration Date'],
  ['rtoName', 'RTO'],
  ['rcStatus', 'RC Status'],
  ['ownership', 'Owner Count'],
  ['chassisNo', 'Chassis No'],
  ['engineNo', 'Engine No'],
  ['cubicCapacity', 'Cubic Capacity'],
  ['seatCapacity', 'Seat Capacity'],
  ['vehicleAge', 'Vehicle Age'],
  ['manufacturerMonthYear', 'Manufactured'],
  ['fitnessUpto', 'Fitness Upto'],
  ['insuranceUpto', 'Insurance Upto'],
  ['insuranceCompany', 'Insurance Company'],
  ['insurancePolicyNumber', 'Insurance Policy'],
  ['pucUpto', 'PUC Upto'],
  ['puccNumber', 'PUC Number'],
  ['financerName', 'Financer'],
  ['presentAddress', 'Present Address'],
  ['permanentAddress', 'Permanent Address'],
];

const prettify = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// DD-MMM-YYYY. toLocaleDateString() rendered "8/10/2026", which reads as
// 8 October to anyone expecting the day-first order this data is filed in.
const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return `${String(d.getDate()).padStart(2, '0')}-${MONTH_ABBR[d.getMonth()]}-${d.getFullYear()}`;
};

const VehicleInfo = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // Blank = no bound, so an admin can filter open-ended on either side.
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useUrlState('page', 1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [isFiltered, setIsFiltered] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchItems = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (statusFilter) params.rcStatus = statusFilter;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const response = await vehicleInfoAPI.getAll(params);
      const d = response.data || {};
      setItems(Array.isArray(d.data) ? d.data : []);
      setTotalPages(d.pagination?.totalPages || 1);
      setTotal(d.pagination?.total || 0);
      setTotalAll(d.pagination?.totalAll ?? d.pagination?.total ?? 0);
      setIsFiltered(Boolean(d.pagination?.filtered));
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, startDate, endDate]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchInput !== search) {
        setSearch(searchInput);
        setPage(1);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput, search, setPage]);

  // Close detail on Escape
  useEffect(() => {
    if (!detail) return;
    const onKey = (e) => { if (e.key === 'Escape') setDetail(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [detail]);

  const openDetail = async (item) => {
    setDetail({ registrationNo: item.registrationNo });
    setDetailLoading(true);
    try {
      const res = await vehicleInfoAPI.getById(item.id);
      setDetail(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to load vehicle');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Drives the Reset control and the empty state's wording — derived from the
  // inputs rather than the response so it is right before the fetch returns.
  const hasFilters = Boolean(searchInput || search || statusFilter || startDate || endDate);

  const resetFilters = () => {
    setSearchInput('');
    setSearch('');
    setStatusFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await vehicleInfoAPI.delete(confirmDelete.id);
      setConfirmDelete(null);
      fetchItems();
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to delete');
      setConfirmDelete(null);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Vehicle Info</h1>
          <p style={styles.subtitle}>Cached RC records — filled by free lookups and paid APIClub lookups.</p>
        </div>
      </div>

      <div style={styles.card}>
        <FilterBar right={<CountPill count={total} total={isFiltered ? totalAll : undefined} noun="vehicle" />}>
          <SearchField
            value={searchInput}
            onChange={setSearchInput}
            placeholder="Search plate, owner, model, chassis or engine"
            width={320}
          />
          <FilterSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            aria-label="RC status"
          >
            <option value="">All RC Statuses</option>
            <option value="ACTIVE">Active</option>
            <option value="NOC ISSUED">NOC Issued</option>
            <option value="SUSPENDED">Suspended</option>
          </FilterSelect>
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onChange={({ startDate: from, endDate: to }) => {
              setStartDate(from);
              setEndDate(to);
              setPage(1);
            }}
          />
          {hasFilters && <ResetButton onClick={resetFilters} />}
        </FilterBar>

        {error && <div style={styles.errorBox}>{error}</div>}

        {loading ? (
          <div style={styles.loadingState}>Loading...</div>
        ) : items.length === 0 ? (
          <div style={styles.emptyState}>
            {hasFilters ? (
              <>
                <p style={{ fontSize: '15px', margin: 0 }}>No vehicles match these filters.</p>
                <p style={{ fontSize: '13px', marginTop: '6px' }}>
                  {totalAll ? `${totalAll.toLocaleString()} records are cached in total.` : 'Try widening the date range or clearing the search.'}
                </p>
                <button type="button" onClick={resetFilters} style={styles.emptyAction}>
                  Reset filters
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: '15px', margin: 0 }}>No vehicles cached yet.</p>
                <p style={{ fontSize: '13px', marginTop: '6px' }}>Records appear here after an RC lookup.</p>
              </>
            )}
          </div>
        ) : (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Number Plate</th>
                  <th style={styles.th}>Owner</th>
                  <th style={styles.th}>Maker / Model</th>
                  <th style={styles.th}>Fuel</th>
                  <th style={styles.th}>RTO</th>
                  <th style={styles.th}>RC Status</th>
                  <th style={styles.th}>Updated</th>
                  <th style={{ ...styles.th, ...styles.thRight }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    <td style={{ ...styles.td, ...styles.plate }}>{item.registrationNo}</td>
                    <td style={styles.td}>{item.ownerName || <span style={styles.muted}>—</span>}</td>
                    <td style={styles.td}>{item.makerModel || <span style={styles.muted}>—</span>}</td>
                    <td style={styles.td}>{item.fuelType || <span style={styles.muted}>—</span>}</td>
                    <td style={styles.td}>{item.rtoName || <span style={styles.muted}>—</span>}</td>
                    <td style={styles.td}>
                      {item.rcStatus ? (
                        <span style={{ ...styles.statusBadge, ...(item.rcStatus.toUpperCase() === 'ACTIVE' ? styles.statusActive : styles.statusOther) }}>
                          {item.rcStatus}
                        </span>
                      ) : <span style={styles.muted}>—</span>}
                    </td>
                    <td style={styles.td}>{formatDate(item.updatedAt)}</td>
                    <td style={styles.tdRight}>
                      <div style={styles.actionButtons}>
                        <button
                          style={styles.iconButton}
                          title="View"
                          onClick={() => openDetail(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#eef2ff'; e.currentTarget.style.color = '#4f46e5'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        </button>
                        <button
                          style={styles.iconButton}
                          title="Delete"
                          onClick={() => setConfirmDelete(item)}
                          onMouseEnter={(e) => { e.currentTarget.style.background = '#fef2f2'; e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.borderColor = '#fecaca'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-2 14a2 2 0 01-2 2H9a2 2 0 01-2-2L5 6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && items.length > 0 && totalPages > 1 && (
          <div style={styles.pagination}>
            <span style={styles.pageInfo}>Page {page} of {totalPages}</span>
            <div style={styles.pageButtons}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ ...styles.pageButton, ...(page <= 1 ? styles.pageButtonDisabled : {}) }}
              >Prev</button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                style={{ ...styles.pageButton, ...(page >= totalPages ? styles.pageButtonDisabled : {}) }}
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {detail && (
        <div style={styles.modal} onClick={() => setDetail(null)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{detail.registrationNo}</h2>
              <button type="button" onClick={() => setDetail(null)} style={styles.closeBtn}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div style={styles.modalBody}>
              {detailLoading ? (
                <div style={styles.loadingState}>Loading...</div>
              ) : (
                <>
                  <div style={styles.grid}>
                    {DETAIL_FIELDS.filter(([k]) => detail[k] !== null && detail[k] !== undefined && detail[k] !== '').map(([k, label]) => (
                      <div key={k} style={styles.field}>
                        <div style={styles.fieldLabel}>{label}</div>
                        <div style={styles.fieldValue}>{String(detail[k])}</div>
                      </div>
                    ))}
                  </div>

                  {detail.extras && Object.keys(detail.extras).length > 0 && (
                    <>
                      <div style={styles.sectionTitle}>Other Details</div>
                      <div style={styles.grid}>
                        {Object.entries(detail.extras).map(([k, v]) => (
                          <div key={k} style={styles.field}>
                            <div style={styles.fieldLabel}>{prettify(k)}</div>
                            <div style={styles.fieldValue}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setDetail(null)} style={styles.secondaryButton}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div style={styles.modal} onClick={() => setConfirmDelete(null)}>
          <div style={{ ...styles.modalContent, maxWidth: '420px' }} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Delete Vehicle?</h2>
            </div>
            <div style={styles.modalBody}>
              <p style={{ fontSize: '14px', color: '#475569', margin: 0 }}>
                This removes the cached record for "<strong>{confirmDelete.registrationNo}</strong>". The next lookup for this
                number will fetch it again from the upstream provider.
              </p>
            </div>
            <div style={styles.modalFooter}>
              <button type="button" onClick={() => setConfirmDelete(null)} style={styles.secondaryButton}>Cancel</button>
              <button type="button" onClick={handleDelete} style={{ ...styles.primaryButton, backgroundColor: '#dc2626' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VehicleInfo;
